import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Plus, Trash, PencilSimple, FloppyDisk, X, UsersThree, Buildings, CaretUp, CaretDown, CaretUpDown } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { BizName } from '@/components/BizName';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployeesSettings() {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ name: '', code: '', department_id: '' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignDeptId, setReassignDeptId] = useState('');
  const [sort, setSort] = useState({ key: 'id_card', dir: 'asc' });
  const [availableEmails, setAvailableEmails] = useState([]);

  const toggleSort = (key) => {
    setSort((p) => (p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  const sortedEmployees = useMemo(() => {
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    return [...employees].sort((a, b) => {
      const av = a[key]; const bv = b[key];
      const ae = av == null || av === '';
      const be = bv == null || bv === '';
      if (ae && be) return 0;
      if (ae) return 1;   // empties always last
      if (be) return -1;
      if (key === 'id_card' || key === 'code') {
        const an = Number(av); const bn = Number(bv);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return (an - bn) * mult;
      }
      return String(av).localeCompare(String(bv), 'ja') * mult;
    });
  }, [employees, sort]);

  const load = async () => {
    setLoading(true);
    try {
      const [empRes, deptRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/departments`),
      ]);
      setEmployees(empRes.data || []);
      setDepartments(deptRes.data || []);
      axios.get(`${API}/employees/available-emails`)
        .then((r) => setAvailableEmails(r.data || []))
        .catch(() => setAvailableEmails([]));
    } catch (e) {
      toast.error('読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing('new');
    setDraft({ name: '', name_en: '', id_card: '', code: '', email: '', department_id: '' });
  };

  const startEdit = (emp) => {
    setEditing(emp.id);
    setDraft({
      name: emp.name || '',
      name_en: emp.name_en || '',
      id_card: emp.id_card || '',
      code: emp.code || '',
      email: emp.email || '',
      department_id: emp.department_id || '',
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft({ name: '', code: '', email: '', department_id: '' });
  };

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error('氏名を入力してください');
      return;
    }
    if (draft.name.trim().length > 25) {
      toast.error('氏名は25文字以内で入力してください');
      return;
    }
    const payload = {
      name: draft.name.trim(),
      name_en: (draft.name_en || '').trim(),
      id_card: (draft.id_card || '').trim(),
      code: (draft.code || '').trim(),
      email: (draft.email || '').trim(),
      department_id: draft.department_id || null,
    };
    try {
      if (editing === 'new') {
        await axios.post(`${API}/employees`, payload);
        toast.success('社員を追加しました');
      } else {
        await axios.put(`${API}/employees/${editing}`, payload);
        toast.success('社員を更新しました');
      }
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || '保存に失敗しました');
    }
  };

  const remove = async (emp) => {
    if (!window.confirm(`「${emp.name}」を削除しますか？`)) return;
    try {
      await axios.delete(`${API}/employees/${emp.id}`);
      toast.success('社員を削除しました');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || '削除に失敗しました');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === employees.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(employees.map((e) => e.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`選択した ${selectedIds.length} 名を削除します。よろしいですか？`)) return;
    try {
      await axios.post(`${API}/employees/bulk-delete`, { ids: selectedIds });
      toast.success(`${selectedIds.length} 名を削除しました`);
      setSelectedIds([]);
      load();
    } catch (e) {
      toast.error('一括削除に失敗しました');
    }
  };

  const handleBulkReassign = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await axios.post(`${API}/employees/bulk-update-department`, {
        ids: selectedIds,
        department_id: reassignDeptId || null,
      });
      toast.success(`${res.data.count} 名を${res.data.department_name ? `「${res.data.department_name}」に` : '未所属に'}変更しました`);
      setReassignDialogOpen(false);
      setReassignDeptId('');
      setSelectedIds([]);
      load();
    } catch (e) {
      toast.error('部署の一括変更に失敗しました');
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2"
            data-testid="settings-page-title">
            <UsersThree size={28} weight="duotone" className="text-teal-700" />
            社員管理
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            社員の氏名・個人コード・所属を管理します。フォーム選択肢に反映されます。
          </p>
        </div>
        {editing !== 'new' && (
          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
              <>
                <span className="text-sm font-medium text-zinc-700" data-testid="bulk-selected-count">{selectedIds.length}名選択中:</span>
                <Button onClick={() => setReassignDialogOpen(true)} data-testid="emp-bulk-reassign" variant="outline">
                  <Buildings size={14} className="mr-1" /> 部署を変更
                </Button>
                <Button onClick={handleBulkDelete} data-testid="emp-bulk-delete" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50">
                  <Trash size={14} className="mr-1" /> 一括削除
                </Button>
              </>
            )}
            <Button onClick={startNew} data-testid="btn-add-employee" className="bg-teal-700 hover:bg-teal-800">
              <Plus size={18} className="mr-1" /> 社員を追加
            </Button>
          </div>
        )}
      </div>

      <div className="hidden md:block bg-white rounded-lg border border-zinc-200 overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead className="bg-zinc-100 border-b-2 border-zinc-300">
            <tr>
              <th className="px-3 py-3 w-10 text-center">
                <Checkbox
                  checked={selectedIds.length > 0 && selectedIds.length === employees.length}
                  onCheckedChange={toggleSelectAll}
                  aria-label="すべての社員を選択"
                  data-testid="emp-select-all"
                />
              </th>
              <SortHeader label="ID" sortKey="id_card" sort={sort} onSort={toggleSort} className="text-left px-4 py-3 text-xs font-semibold text-zinc-700 w-24 whitespace-nowrap" />
              <SortHeader label="氏名" sortKey="name" sort={sort} onSort={toggleSort} className="text-left px-4 py-3 text-xs font-semibold text-zinc-700 w-48 whitespace-nowrap" />
              <SortHeader label="個人コード" sortKey="code" sort={sort} onSort={toggleSort} className="text-left px-4 py-3 text-xs font-semibold text-zinc-700 w-28 whitespace-nowrap" />
              <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-700">メール</th>
              <SortHeader label="所属" sortKey="department_name" sort={sort} onSort={toggleSort} className="text-left px-4 py-3 text-xs font-semibold text-zinc-700 w-44 whitespace-nowrap" />
              <th className="text-right px-4 py-3 text-xs font-semibold text-zinc-700 w-40 whitespace-nowrap">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {editing === 'new' && (
              <EditRow draft={draft} setDraft={setDraft} departments={departments} availableEmails={availableEmails} onSave={save} onCancel={cancelEdit} />
            )}
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">読み込み中...</td></tr>
            ) : employees.length === 0 && editing !== 'new' ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">社員が登録されていません</td></tr>
            ) : (
              sortedEmployees.map((emp, idx) => (
                editing === emp.id ? (
                  <EditRow key={emp.id} draft={draft} setDraft={setDraft} departments={departments} availableEmails={availableEmails} onSave={save} onCancel={cancelEdit} />
                ) : (
                  <tr key={emp.id} className={`hover:bg-zinc-50 transition-colors ${selectedIds.includes(emp.id) ? 'bg-teal-50/50' : ''}`} data-testid={`emp-row-${emp.id}`}>
                    <td className="px-3 py-3 text-center">
                      <Checkbox
                        checked={selectedIds.includes(emp.id)}
                        onCheckedChange={() => toggleSelect(emp.id)}
                        aria-label={`${emp.name || emp.id_card} を選択`}
                        data-testid={`emp-select-${emp.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-mono tabular-nums text-zinc-700 whitespace-nowrap" data-testid={`emp-id-${emp.id}`}>
                      {emp.id_card || <span className="text-zinc-400 italic font-sans">未設定</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-zinc-900">
                      <BizName ja={emp.name} en={emp.name_en} testid={`emp-name-${emp.id}`} />
                    </td>
                    <td className="px-4 py-3 text-sm font-mono tabular-nums text-zinc-700 whitespace-nowrap" data-testid={`emp-code-${emp.id}`}>
                      {emp.code || <span className="text-zinc-400 italic font-sans">未設定</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600 truncate max-w-[16rem]" data-testid={`emp-email-${emp.id}`}>
                      {emp.email || <span className="text-zinc-400 italic">未設定</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600 whitespace-nowrap">
                      {emp.department_name || <span className="text-zinc-400 italic">未所属</span>}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => startEdit(emp)} data-testid={`btn-edit-emp-${emp.id}`}>
                        <PencilSimple size={14} className="mr-1" /> 編集
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => remove(emp)} data-testid={`btn-delete-emp-${emp.id}`}
                        aria-label={`${emp.name || emp.id_card} を削除`} title="この社員を削除します（元に戻せません）"
                        className="text-red-600 hover:bg-red-50 h-9 w-9 p-0">
                        <Trash size={14} />
                      </Button>
                    </td>
                  </tr>
                )
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Mobile card list ── */}
      <div className="md:hidden space-y-3" data-testid="employees-mobile-list">
        {editing === 'new' && (
          <MobileEmpEditCard draft={draft} setDraft={setDraft} departments={departments} availableEmails={availableEmails} onSave={save} onCancel={cancelEdit} />
        )}
        {loading ? (
          <div className="bg-white rounded-lg border border-zinc-200 px-4 py-8 text-center text-sm text-zinc-500">読み込み中...</div>
        ) : employees.length === 0 && editing !== 'new' ? (
          <div className="bg-white rounded-lg border border-zinc-200 px-4 py-8 text-center text-sm text-zinc-500">社員が登録されていません</div>
        ) : (
          sortedEmployees.map((emp) => (
            editing === emp.id ? (
              <MobileEmpEditCard key={emp.id} draft={draft} setDraft={setDraft} departments={departments} availableEmails={availableEmails} onSave={save} onCancel={cancelEdit} />
            ) : (
              <div key={emp.id} className={`bg-white rounded-lg border p-4 ${selectedIds.includes(emp.id) ? 'border-blue-300 bg-teal-50/50' : 'border-zinc-200'}`} data-testid={`emp-card-${emp.id}`}>
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={selectedIds.includes(emp.id)}
                    onCheckedChange={() => toggleSelect(emp.id)}
                    data-testid={`emp-select-${emp.id}-m`}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-semibold text-zinc-900">
                      <BizName ja={emp.name} en={emp.name_en} testid={`emp-name-${emp.id}-m`} />
                    </p>
                    <p className="text-xs text-zinc-500 font-mono tabular-nums mt-0.5">
                      ID {emp.id_card || '—'} ・ コード {emp.code || '—'}
                    </p>
                  </div>
                </div>
                <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2">
                  <div className="col-span-2">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">メール</p>
                    <p className="text-sm text-zinc-700 break-all">{emp.email || <span className="text-zinc-400 italic">未設定</span>}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">所属</p>
                    <p className="text-sm text-zinc-700">{emp.department_name || <span className="text-zinc-400 italic">未所属</span>}</p>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => startEdit(emp)} data-testid={`btn-edit-emp-${emp.id}-m`}>
                    <PencilSimple size={14} className="mr-1" /> 編集
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(emp)} data-testid={`btn-delete-emp-${emp.id}-m`}
                    aria-label={`${emp.name || emp.id_card} を削除`} title="この社員を削除します（元に戻せません）"
                    className="text-red-600 hover:bg-red-50 h-9 w-9 p-0">
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            )
          ))
        )}
      </div>

      {/* Bulk Reassign Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle>部署の一括変更</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-zinc-600">
              選択した <span className="font-bold">{selectedIds.length}</span> 名の所属部署を変更します。
            </p>
            <div>
              <Label className="font-bold">新しい所属部署</Label>
              <Select value={reassignDeptId || '__none__'} onValueChange={(v) => setReassignDeptId(v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid="select-reassign-dept"><SelectValue placeholder="部署を選択" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">（未所属に変更）</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignDialogOpen(false)} data-testid="btn-reassign-cancel">キャンセル</Button>
            <Button onClick={handleBulkReassign} data-testid="btn-reassign-confirm" className="bg-teal-700 hover:bg-teal-800">変更を適用</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SortHeader({ label, sortKey, sort, onSort, className }) {
  const active = sort.key === sortKey;
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`emp-sort-${sortKey}`}
        aria-label={`${label} で並べ替え`}
        title={`${label} で並べ替え`}
        className={`inline-flex items-center gap-1 min-h-[32px] min-w-[32px] select-none transition-colors hover:text-zinc-900 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none ${active ? 'text-zinc-900' : ''}`}
      >
        {label}
        {active
          ? (sort.dir === 'asc' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />)
          : <CaretUpDown size={12} className="text-zinc-300" />}
      </button>
    </th>
  );
}

function EditRow({ draft, setDraft, departments, availableEmails = [], onSave, onCancel }) {
  // Suggestions = unlinked company emails + whatever this employee already has.
  const emailOptions = [...new Set([...(draft.email ? [draft.email] : []), ...availableEmails])];
  return (
    <tr className="bg-blue-50/30">
      <td className="px-3 py-3" />
      <td className="px-4 py-3">
        <Input
          value={draft.id_card || ''}
          onChange={(e) => setDraft({ ...draft, id_card: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) })}
          placeholder="0000"
          inputMode="numeric"
          maxLength={4}
          data-testid="input-emp-id-card"
          className="font-mono tabular-nums"
        />
      </td>
      <td className="px-4 py-3">
        <Input
          autoFocus
          maxLength={25}
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="例: 田中 太郎"
          data-testid="input-emp-name"
        />
        <p className="text-xs text-zinc-400 mt-1">{(draft.name || '').length} / 25</p>
        <Input
          maxLength={60}
          value={draft.name_en || ''}
          onChange={(e) => setDraft({ ...draft, name_en: e.target.value })}
          placeholder="英語表記（任意）例: Taro Tanaka"
          data-testid="input-emp-name-en"
          className="mt-1.5 h-9"
        />
      </td>
      <td className="px-4 py-3">
        <Input
          value={draft.code}
          onChange={(e) => setDraft({ ...draft, code: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) })}
          placeholder="0000"
          inputMode="numeric"
          maxLength={4}
          data-testid="input-emp-code"
          className="font-mono tabular-nums"
        />
      </td>
      <td className="px-4 py-3">
        <Select value={emailOptions.includes(draft.email) ? draft.email : ''} onValueChange={(v) => setDraft({ ...draft, email: v })}>
          <SelectTrigger className="h-9" data-testid="emp-email-select">
            <SelectValue placeholder={availableEmails.length ? '会社メールから選択' : '未割当の会社メールなし'} />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {emailOptions.length === 0 ? (
              <div className="px-2 py-2 text-xs text-zinc-400">未割当の会社メールがありません</div>
            ) : emailOptions.map((em) => (
              <SelectItem key={em} value={em} data-testid={`emp-email-opt-${em}`}>{em}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="email"
          value={draft.email || ''}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          placeholder="または手動で入力"
          data-testid="input-emp-email"
          className="h-9 mt-1.5"
        />
      </td>
      <td className="px-4 py-3">
        <Select
          value={draft.department_id || '__none__'}
          onValueChange={(val) => setDraft({ ...draft, department_id: val === '__none__' ? '' : val })}
        >
          <SelectTrigger data-testid="select-emp-department"><SelectValue placeholder="部署を選択" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">（未所属）</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
        <Button size="sm" onClick={onSave} data-testid="btn-save-emp" className="bg-teal-700 hover:bg-teal-800">
          <FloppyDisk size={14} className="mr-1" /> 保存
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} data-testid="btn-cancel-emp">
          <X size={14} />
        </Button>
      </td>
    </tr>
  );
}

function MobileEmpEditCard({ draft, setDraft, departments, availableEmails = [], onSave, onCancel }) {
  const emailOptions = [...new Set([...(draft.email ? [draft.email] : []), ...availableEmails])];
  return (
    <div className="bg-white rounded-lg border-2 border-blue-200 p-4 space-y-3" data-testid="emp-mobile-edit-card">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold text-zinc-600">ID</Label>
          <Input
            value={draft.id_card || ''}
            onChange={(e) => setDraft({ ...draft, id_card: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) })}
            placeholder="0000"
            inputMode="numeric"
            maxLength={4}
            data-testid="input-emp-id-card-m"
            className="font-mono tabular-nums mt-1"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold text-zinc-600">個人コード</Label>
          <Input
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) })}
            placeholder="0000"
            inputMode="numeric"
            maxLength={4}
            data-testid="input-emp-code-m"
            className="font-mono tabular-nums mt-1"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs font-semibold text-zinc-600">氏名</Label>
        <Input
          autoFocus
          maxLength={25}
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="例: 田中 太郎"
          data-testid="input-emp-name-m"
          className="mt-1"
        />
        <p className="text-xs text-zinc-400 mt-1">{(draft.name || '').length} / 25</p>
      </div>
      <div>
        <Label className="text-xs font-semibold text-zinc-600">英語表記（任意）</Label>
        <Input
          maxLength={60}
          value={draft.name_en || ''}
          onChange={(e) => setDraft({ ...draft, name_en: e.target.value })}
          placeholder="例: Taro Tanaka"
          data-testid="input-emp-name-en-m"
          className="mt-1"
        />
      </div>
      <div>
        <Label className="text-xs font-semibold text-zinc-600">メール</Label>
        <Select value={emailOptions.includes(draft.email) ? draft.email : ''} onValueChange={(v) => setDraft({ ...draft, email: v })}>
          <SelectTrigger className="h-9 mt-1" data-testid="emp-email-select-m">
            <SelectValue placeholder={availableEmails.length ? '会社メールから選択' : '未割当の会社メールなし'} />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {emailOptions.length === 0 ? (
              <div className="px-2 py-2 text-xs text-zinc-400">未割当の会社メールがありません</div>
            ) : emailOptions.map((em) => (
              <SelectItem key={em} value={em}>{em}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="email"
          value={draft.email || ''}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          placeholder="または手動で入力"
          data-testid="input-emp-email-m"
          className="h-9 mt-1.5"
        />
      </div>
      <div>
        <Label className="text-xs font-semibold text-zinc-600">所属</Label>
        <Select
          value={draft.department_id || '__none__'}
          onValueChange={(val) => setDraft({ ...draft, department_id: val === '__none__' ? '' : val })}
        >
          <SelectTrigger className="mt-1" data-testid="select-emp-department-m"><SelectValue placeholder="部署を選択" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">（未所属）</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={onSave} className="flex-1 bg-teal-700 hover:bg-teal-800" data-testid="btn-save-emp-m">
          <FloppyDisk size={14} className="mr-1" /> 保存
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} data-testid="btn-cancel-emp-m">
          <X size={14} />
        </Button>
      </div>
    </div>
  );
}
